import { FC, useState, memo, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, keys } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectUserData } from 'store/slices/userData';
// Import Config
import { dataTableColumnsConfigs } from './config';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Components
import DataTable from 'components/common/DataTable';
// Import UI Components
import { Dropdown, SkeletonLoader } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import styles from './permissionsList.module.scss';

interface PermissionItem {
    hasHover: boolean;
    dragDisable: boolean;
    data: {
        Permission: string;
        Availability: boolean;
    };
}

interface Props {
    userId: number | null;
}

const UserPermissionsList: FC<Props> = ({ userId }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { ActivePartner, UserId } = useAppSelector(selectUserData) ?? {};
    const PartnerId = ActivePartner?.PartnerId;

    const { getUserPartnersRequest, getUserPartnerPermissionsRequest, getCurrentUserPartnerPermissionsRequest } =
        useCancelableRequests(
            {
                getUserPartnersRequest: UtilsHttpService.getUserPartners,
                getUserPartnerPermissionsRequest: UtilsHttpService.getUserPartnerPermissions,
                getCurrentUserPartnerPermissionsRequest: UtilsHttpService.getCurrentUserPartnerPermissions,
            },
            {
                takeLatest: [
                    'getUserPartnersRequest',
                    'getUserPartnerPermissionsRequest',
                    'getCurrentUserPartnerPermissionsRequest',
                ],
            },
        );

    const { columns } = useMemo(() => dataTableColumnsConfigs(t), [t]);

    const [userPermissionsListData, setUserPermissionsListData] = useState<PermissionItem[]>([]);
    const [isUserPermissionsListLoading, setIsUserPermissionsListLoading] = useState(false);

    const [selectedPartnerId, setSelectedPartnerId] = useState(null);
    const [userPartnersDropdownData, setUserPartnersDropdownData] = useState([]);
    const [isPartnersListLoading, setIsPartnersListLoading] = useState(false);

    const mapToPartnersDropdownModel = (data) => {
        return data.map((item) => ({
            label: `${item.Name} (${item.PartnerId})`,
            value: item.PartnerId,
        }));
    };

    const init = () => {
        if (!isNil(userId)) {
            setIsPartnersListLoading(true);
            doGetRequest(getUserPartnersRequest.request, {
                queryString: { userId },
                successCallback: (data) => {
                    setUserPartnersDropdownData(mapToPartnersDropdownModel(data));
                    const defaultPartner = data.find((partner) => partner.PartnerId === PartnerId);
                    if (defaultPartner) {
                        setSelectedPartnerId(defaultPartner.PartnerId);
                    } else {
                        setSelectedPartnerId(data[0]?.PartnerId);
                    }
                    setIsPartnersListLoading(false);
                },
                errorCallback: () => {
                    setIsPartnersListLoading(false);
                },
            });
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [userId]);

    const getTableRows = (permissions) => {
        return keys(permissions).map((permission) => {
            return {
                hasHover: true,
                dragDisable: true,
                data: {
                    Permission: permission,
                    Availability: permissions[permission],
                },
            };
        });
    };

    useEffect(() => {
        if (!isNil(selectedPartnerId)) {
            setIsUserPermissionsListLoading(true);
            const isCurrentUser = userId === UserId;
            const request = isCurrentUser
                ? getCurrentUserPartnerPermissionsRequest.request
                : getUserPartnerPermissionsRequest.request;
            const queryString = {
                partnerId: selectedPartnerId,
                ...(isCurrentUser ? {} : { userId }),
            };
            doGetRequest(request, {
                queryString,
                successCallback: (data) => {
                    setUserPermissionsListData(getTableRows(data));
                    setIsUserPermissionsListLoading(false);
                },
                errorCallback: () => {
                    setIsUserPermissionsListLoading(false);
                },
            });
        }
    }, [
        UserId,
        doGetRequest,
        getCurrentUserPartnerPermissionsRequest.request,
        getUserPartnerPermissionsRequest.request,
        selectedPartnerId,
        userId,
    ]);

    const onDropdownChange = (selectedPartner) => {
        setSelectedPartnerId(selectedPartner.value);
    };

    const getHeaderActions = () => {
        return (
            <div className={styles.headerActions}>
                <SkeletonLoader isBusy={isPartnersListLoading}>
                    <Dropdown
                        label={l.Partner}
                        labelAppearance="swap"
                        value={selectedPartnerId}
                        onChange={onDropdownChange}
                        data={userPartnersDropdownData}
                        hasSearch={true}
                        placeholder={l.Partner}
                        isLoading={isPartnersListLoading}
                    />
                </SkeletonLoader>
            </div>
        );
    };

    return (
        <DataTable
            rowKey="Permission"
            columnKey="dataKey"
            name={t(l.PermissionsList)}
            isLoading={isUserPermissionsListLoading}
            data={userPermissionsListData}
            dataCount={userPermissionsListData?.length ?? 0}
            columns={columns}
            isRowExpandable={false}
            isColumnsSortable={false}
            isShowColumnChooser={false}
            isShowVerticalDots={false}
            withPagination={false}
            withPageSelector={false}
            headerActions={getHeaderActions()}
        />
    );
};

export default memo(UserPermissionsList);
